import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { ChevronDown } from 'lucide-react';
import { ArrowUpRightCircle } from 'react-bootstrap-icons';
import { makeRESTApiRequest } from '../../helpers/api';
import { ADMIN_ROLE, ENDPOINTS, ORDER_STATUS_RANGE, PROCESSOR_ROLE } from '../../helpers/constants';
import { getUserFromLocalstrorage } from '../../helpers/global';

const SERVICES = ['All Services', 'Lien Search', 'Estoppel', 'Survey'];
const STATES = [
  { value: 'active', label: 'Active' },
  { value: 'delivered', label: 'Delivered' }
];

const COLUMNS = {
  active: [
    { label: 'New Order', keys: ['New Order'], color: '#3b82f6' },
    { label: 'Due Diligence', keys: ['Due Diligence'], color: '#14b8a6' },
    { label: 'Update', keys: ['Update'], color: '#10b981' },
    { label: 'Info Requested', keys: ['Information Requested'], color: '#f59e0b' },
    { label: 'Preliminary Report', keys: ['Preliminary Report'], color: '#7C6AE8' },
    { label: 'Info Received', keys: ['Information Received'], color: '#8b5cf6' },
    { label: 'Ready for QA', keys: ['Ready for QA'], color: '#06b6d4' },
    { label: 'Revision', keys: ['Revision'], color: '#ec4899' },
    { label: 'Link Sent', keys: ['Link Sent'], color: '#6366f1' },
    { label: 'Hold', keys: ['Hold'], color: '#9ca3af' },
    { label: 'Refund Requested', keys: ['Refund Requested'], color: '#ef4444' }
  ],
  delivered: [
    {
      label: 'Delivered Not Paid',
      keys: ['__deliveredNotPaidLast30__'],
      color: '#f59e0b',
      deliveredRange: ORDER_STATUS_RANGE.LAST30DAYS
    },
    {
      label: 'Delivered Overdue',
      keys: ['__deliveredOverdue__'],
      color: '#ef4444',
      deliveredRange: ORDER_STATUS_RANGE.MORETHAN30DAYS
    },
    { label: 'Paid', keys: ['Paid'], color: '#10b981' },
    { label: 'Refunded', keys: ['Refunded'], color: '#8b5cf6' },
    { label: 'Canceled', keys: ['Cancelled'], color: '#9ca3af' }
  ]
};

function statusLookup(statusRows) {
  if (!Array.isArray(statusRows)) return {};
  return Object.fromEntries(statusRows.filter(r => r?._id != null).map(r => [r._id, r.count ?? 0]));
}

function matchColumn(col, lookup) {
  const key = col.keys?.find(k => Object.prototype.hasOwnProperty.call(lookup, k));
  if (key != null) {
    return { key, count: lookup[key] ?? 0 };
  }
  const fallbackKey = col.keys?.[0];
  if (fallbackKey == null) return null;
  return { key: fallbackKey, count: 0 };
}

const DD_WIDTH = '168px';
const DD_TOGGLE = {
  backgroundColor: '#f6f7f9',
  color: '#1e293b',
  border: '1px solid #dee2e6',
  fontSize: '0.875rem',
  width: DD_WIDTH,
  minWidth: DD_WIDTH,
  maxWidth: DD_WIDTH,
  boxSizing: 'border-box',
  borderRadius: '10px',
  boxShadow: 'none',
  padding: '0.45rem 0.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0,
  fontWeight: 400,
  lineHeight: 1.5
};
const DD_MENU = {
  padding: 4,
  width: DD_WIDTH,
  minWidth: DD_WIDTH,
  maxWidth: DD_WIDTH,
  boxSizing: 'border-box',
  border: '1px solid #e9ecef',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
};

function FilterDropdown({ id, label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const text = typeof options[0] === 'string' ? value : options.find(o => o.value === value)?.label ?? '';

  return (
    <Dropdown show={open} onToggle={setOpen} className="ml-0">
      <Dropdown.Toggle
        variant="light"
        id={id}
        aria-label={label}
        className="d-flex align-items-center shadow-none order-pipeline-dd-toggle"
        style={DD_TOGGLE}
      >
        <span className="text-left text-truncate" style={{ minWidth: 0, flex: '1 1 auto' }}>
          {text}
        </span>
        <ChevronDown size={16} strokeWidth={2} className="flex-shrink-0" aria-hidden style={{ color: '#9ca3af' }} />
      </Dropdown.Toggle>
      <Dropdown.Menu align="end" style={DD_MENU}>
        {options.map(opt => {
          const v = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          const sel = v === value;
          return (
            <Dropdown.Item
              key={String(v)}
              className={`order-pipeline-dd-item ${sel ? 'order-pipeline-dd-item--sel' : ''}`}
              onClick={e => {
                e.preventDefault();
                onChange(v);
                setOpen(false);
              }}
            >
              {lbl}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function StatCard({ col, lookup, serviceFilter, organizationIds, assignedProcessorId }) {
  const m = matchColumn(col, lookup);
  if (!m) return null;
  const svc = encodeURIComponent(serviceFilter || 'All Services');
  const orgPrefix =
    organizationIds && organizationIds.length > 0
      ? `${organizationIds.map(id => `organizationId=${encodeURIComponent(id)}`).join('&')}&`
      : '';
  const assignedProcessorQuery = assignedProcessorId
    ? `&assignedProcessorId=${encodeURIComponent(assignedProcessorId)}`
    : '';
  const pipelineTo = col.deliveredRange
    ? `/services/pipeline?${orgPrefix}orderStatus=${encodeURIComponent('Pending To Pay')}&range=${
        col.deliveredRange
      }&service=${svc}${assignedProcessorQuery}`
    : `/services/pipeline?${orgPrefix}orderStatus=${encodeURIComponent(m.key)}&service=${svc}${assignedProcessorQuery}`;
  return (
    <Link
      to={pipelineTo}
      target="_blank"
      rel="noopener noreferrer"
      className="order-pipeline-stat-card bg-white p-2 p-md-3 d-flex flex-column text-decoration-none"
      style={{ color: '#0f172a' }}
      aria-label={`${col.label}: ${m.count.toLocaleString()}, open pipeline`}
    >
      <div className="d-flex flex-column align-items-start text-left flex-grow-1 w-100">
        <div className="d-flex align-items-center justify-content-between w-100 mb-2">
          <div
            className="rounded-circle flex-shrink-0"
            style={{
              width: '0.75rem',
              height: '0.75rem',
              backgroundColor: col.color,
              boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06)'
            }}
          />
          <ArrowUpRightCircle color="currentColor" size={18} className="flex-shrink-0" aria-hidden />
        </div>
        <span
          className="d-block fw-bold"
          style={{ fontSize: '1.5rem', lineHeight: 2, fontWeight: 700, color: '#0f172a' }}
        >
          {m.count.toLocaleString()}
        </span>
        <span className="d-block mt-1" style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: '1rem' }}>
          {col.label}
        </span>
      </div>
    </Link>
  );
}

function SkeletonStatCard() {
  const shimmer = 'order-pipeline-skeleton-shimmer';
  return (
    <div
      className="order-pipeline-stat-card order-pipeline-skeleton-card bg-white p-2 p-md-3 d-flex flex-column"
      aria-hidden
    >
      <div className="d-flex flex-column align-items-start w-100 flex-grow-1">
        <div className="d-flex align-items-center justify-content-between w-100 mb-2">
          <div className={`rounded-circle flex-shrink-0 ${shimmer}`} style={{ width: '0.75rem', height: '0.75rem' }} />
          <div
            className={`rounded-circle flex-shrink-0 ${shimmer}`}
            style={{ width: '1.125rem', height: '1.125rem' }}
          />
        </div>
        <div className={`${shimmer} rounded mb-2`} style={{ width: '55%', minWidth: '2.5rem', height: '1.75rem' }} />
        <div className={`${shimmer} rounded`} style={{ width: '88%', height: '0.875rem' }} />
      </div>
    </div>
  );
}

export default function OrderStatusPipeline({ organizationIds = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('All Services');
  const [stateFilter, setStateFilter] = useState('active');

  const currentRole = getUserFromLocalstrorage()?.role;
  const currentUserId = getUserFromLocalstrorage()?._id;
  const assignedProcessorId = currentRole === PROCESSOR_ROLE ? currentUserId : null;
  const isDashboardStaffUser = [ADMIN_ROLE, PROCESSOR_ROLE].includes(currentRole);
  const orgFilterKey = JSON.stringify(organizationIds);

  useEffect(() => {
    if (!isDashboardStaffUser) {
      setData(null);
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      const service = serviceFilter || 'All Services';
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_STATUS_BREAKDOWN,
        method: 'POST',
        requestBody: {
          organizationIds,
          service
        }
      });
      if (!alive) return;
      if (response?.data) setData(response.data);
      else if (error) setData(response?.data);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [isDashboardStaffUser, serviceFilter, orgFilterKey]);

  const lookup = {
    ...statusLookup(data?.status),
    ...(data?.deliveredUnpaid && {
      __deliveredNotPaidLast30__: data.deliveredUnpaid.last30Days?.totalCount ?? 0,
      __deliveredOverdue__: data.deliveredUnpaid.over30Days?.totalCount ?? 0
    })
  };
  const cols = COLUMNS[stateFilter];

  if (!isDashboardStaffUser) return null;

  return (
    <div className="mb-4">
      <div className="order-pipeline-panel p-3 p-md-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-2">
          <h4 className="fw-bold mb-1 mb-md-0 pr-md-2">Order Status Pipeline</h4>
          <div
            className="d-flex flex-nowrap flex-shrink-0 align-items-center ml-md-2 justify-content-end"
            style={{ gap: 12 }}
          >
            <FilterDropdown
              id="order-pipeline-service-filter"
              label="Service filter"
              value={serviceFilter}
              onChange={setServiceFilter}
              options={SERVICES}
            />
            <FilterDropdown
              id="order-pipeline-state-filter"
              label="Active or Delivered"
              value={stateFilter}
              onChange={setStateFilter}
              options={STATES}
            />
          </div>
        </div>

        <div className="w-100 pb-1" style={{ minHeight: 152 }}>
          {loading ? (
            <div
              className="order-pipeline-stat-scroll"
              role="status"
              aria-busy="true"
              aria-label="Loading order status pipeline"
            >
              <div
                className="w-100 order-pipeline-stat-grid"
                style={{ '--order-pipeline-cols': cols.length }}
              >
                {Array.from({ length: cols.length }).map((_, i) => (
                  <SkeletonStatCard key={i} />
                ))}
              </div>
            </div>
          ) : data ? (
            <div className="order-pipeline-stat-scroll">
              <div
                className="w-100 order-pipeline-stat-grid"
                style={{ '--order-pipeline-cols': cols.length }}
              >
                {cols.map(col => (
                  <StatCard
                    key={col.label}
                    col={col}
                    lookup={lookup}
                    serviceFilter={serviceFilter}
                    organizationIds={organizationIds}
                    assignedProcessorId={assignedProcessorId}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div
              className="d-flex justify-content-center align-items-center text-muted small w-100"
              style={{ minHeight: 152 }}
            >
              No status data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
